import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, expand, reduce, scan, takeWhile } from 'rxjs/operators';
import { api } from '../../../models/api-url.model';
import { AccountDeviceData } from '../../../unusual-network-metrics/unusual-network-metrics.model';
import { ApiService } from '../api';
import type {
  AggregateLeadResponse,
  DeviceLocationsResponse,
  GetHistoricalObservationsCommand,
  GetHistoricalObservationsResponse,
  SynapseQueryParams,
} from './synapse.types';

@Injectable({
  providedIn: 'root',
})
export class SynapseService {
  private readonly apiService = inject(ApiService);

  getIQTicketSummary(ticketNumber: string): Observable<any[]> {
    return this.apiService.get(`${api.ticketSummaryURL}/${ticketNumber}`);
  }

  getUnusualMetrics(params: SynapseQueryParams): Observable<any[]> {
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getUnusualMetrics, { params: httpParams });
  }

  getQOELeads(params: SynapseQueryParams): Observable<any[]> {
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getQOELeadsURL, { params: httpParams });
  }

  getQOELeadsAggregate(
    time: string,
    showMockData: boolean,
    accountName: string,
  ): Observable<AccountDeviceData> {
    const params = {
      accountName,
      time,
      showMockData,
    };
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getQOEAggregates, { params: httpParams });
  }

  // Method to loop the calls until an empty array or error
  fetchAllUnusualMetrics(
    time: string,
    showMockData: boolean,
    accountName: string,
  ): Observable<any[]> {
    const params = {
      accountName,
      time,
      showMockData,
      page: 1, // Start with page 1
    };

    return this.getUnusualMetrics(params).pipe(
      expand((records) => {
        if (!records || records.length === 0) {
          // Stop expanding if an empty array is returned
          return of([]);
        }
        params.page += 1; // Increment the page for the next call
        return this.getUnusualMetrics(params);
      }),
      takeWhile((records) => records?.length > 0, true), // Include the last response even if it's empty
      reduce((allRecords, currentRecords) => {
        // Accumulate all records
        if (currentRecords?.length) {
          return allRecords.concat(currentRecords);
        }
        return allRecords;
      }, []),
      catchError((error) => {
        console.error('Error fetching metrics:', error);
        return of([]); // Return an empty array on error
      }),
    );
  }

  fetchAllLeads(
    time: string,
    showMockData: boolean,
    accountName: string,
  ): Observable<any[]> {
    const params = {
      accountName,
      time,
      showMockData,
      page: 1, // Start with page 1
    };
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getQOELeadsURL, { params: httpParams });
  }

  getMetricDetails(params: SynapseQueryParams): Observable<any[]> {
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getMetricDetails, { params: httpParams });
  }

  fetchAllMetricDetails(
    accountName: string,
    time: string,
    dimension: string,
    showMockData: boolean,
  ): Observable<any[]> {
    const params = {
      accountName,
      time,
      dimension,
      showMockData,
      page: 1,
    };

    return this.getMetricDetails(params).pipe(
      expand((records) => {
        if (!records || records.length === 0) {
          // Stop expanding if an empty array is returned
          return of([]);
        }
        params.page += 1; // Increment the page for the next call
        return this.getMetricDetails(params);
      }),
      takeWhile((records) => records?.length > 0, true), // Include the last response even if it's empty
      scan((allRecords, currentRecords) => {
        // Accumulate all records and emit incremental snapshots
        if (currentRecords?.length) {
          return allRecords.concat(currentRecords);
        }
        return allRecords;
      }, []),
      catchError((error) => {
        console.error('Error fetching metric details:', error);
        return of([]); // Return an empty array on error
      }),
    );
  }

  getAggregatedMetrics(
    accountName: string,
    time: string,
    dimension: string,
    metric: string,
    sortBy: string,
    showMockData: boolean,
  ): Observable<any[]> {
    const params = {
      accountName,
      time,
      dimension,
      metric: metric || metric,
      showMockData,
      orderBy: sortBy,
    };
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getAggregatedMetrics, {
      params: httpParams,
    });
  }

  getAnomaliedLeadsAggregate(
    accountName: string,
    time: string,
    showMockData: boolean,
  ): Observable<AggregateLeadResponse[]> {
    const params = {
      accountName,
      time,
      showMockData,
    };
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getAnomaliedLeadsAggregate, {
      params: httpParams,
    });
  }

  getAnomaliedLeads(
    accountName: string,
    time: string,
    showMockData: boolean,
  ): Observable<any[]> {
    const params = {
      accountName,
      time,
      showMockData,
    };
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getAnomaliedLeads, { params: httpParams });
  }

  getDeviceLocations(
    params: SynapseQueryParams,
  ): Observable<DeviceLocationsResponse> {
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getDeviceLocationsURL, {
      params: httpParams,
    });
  }

  getUnmDeviceEvents(
    accountName: string,
    time: string,
    showMockData: boolean,
  ): Observable<AccountDeviceData> {
    const params = {
      accountName,
      time,
      showMockData,
    };
    let httpParams = new HttpParams();
    Object.keys(params).forEach((key) => {
      httpParams = httpParams.set(key, params[key]);
    });
    return this.apiService.get(api.getUnmDeviceEventsURL, {
      params: httpParams,
    });
  }

  getHistoricalObservations(
    options: GetHistoricalObservationsCommand,
  ): Observable<GetHistoricalObservationsResponse> {
    let httpParams = new HttpParams();
    httpParams = httpParams.set('pageNumber', options.pageNumber.toString());
    httpParams = httpParams.set('pageSize', options.pageSize.toString());

    if (options.search) {
      httpParams = httpParams.set('search', options.search.trim());
    }
    if (options.actionLabels?.length > 0) {
      const actionLabels = options.actionLabels
        .map((label) => label.toUpperCase())
        .join(',');
      httpParams = httpParams.set('actionLabels', actionLabels);
    }
    if (options.sort !== undefined) {
      httpParams = httpParams.set('sortOrder', options.sort.order);
      httpParams = httpParams.set('sortBy', options.sort.by);
    }

    return this.apiService.get(api.getHistoricalObservationsURL, {
      params: httpParams,
    });
  }
}
